import { Box, BoxProps, lgBumps } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import {
  CustomerAccount,
  isCustomerAccount,
  PaymentMethod,
  Policy,
  PolicyStatus,
} from '../../domain'
import { isScheme, ProductConfig, Scheme } from '../../state/configuration'
import { PaymentMethodsProvider } from '../product/policy/billing/PaymentMethodsProvider'
import { PolicyProvider } from '../product/policy/PolicyProvider'
import { ProductContext } from '../product/productContext'
import { getPolicyCard } from './cards'

const policyStatusOrder = Object.values(PolicyStatus)

export interface PolicyInformation {
  policy: Policy
  scheme: Scheme
  product: ProductConfig
  customerAccount: CustomerAccount
  paymentMethod?: PaymentMethod
}

export interface PoliciesViewProps extends BoxProps {
  paymentMethods: PaymentMethod[]
  policyInformation: PolicyInformation[]
}

const sortPolicies = (left: PolicyInformation, right: PolicyInformation) => {
  const statusComparison =
    policyStatusOrder.indexOf(left.policy.policyDetail.policyStatus) -
    policyStatusOrder.indexOf(right.policy.policyDetail.policyStatus)

  return statusComparison
    ? statusComparison
    : -left.policy.policyDetail.inceptedAt.localeCompare(
        right.policy.policyDetail.inceptedAt,
      )
}

export const isPolicyInformation = (
  info: Partial<PolicyInformation>,
): info is PolicyInformation =>
  Boolean(info.policy && info.product && info.scheme && info.customerAccount)

export const getPolicyInformation =
  (
    schemes: Scheme[],
    products: Record<string, ProductConfig>,
    customerAccounts: CustomerAccount[],
  ) =>
  (policies: Policy[]): PolicyInformation[] =>
    policies
      .map((policy) => ({
        policy,
        product: products[policy.schemeCode],
        scheme: schemes.find(isScheme(policy.schemeCode)),
        customerAccount: customerAccounts.find(isCustomerAccount(policy.policyNumber)),
      }))
      .filter(isPolicyInformation)

export const PoliciesView: FunctionComponent<PoliciesViewProps> = ({
  policyInformation,
  paymentMethods,
  ...props
}) => {
  const sections = policyInformation
    .sort(sortPolicies)
    .map(({ policy, product, scheme, customerAccount }) => {
      const PolicyCard = getPolicyCard(policy)

      return (
        <PaymentMethodsProvider paymentMethods={paymentMethods}>
          <ProductContext.Provider value={{ scheme, product }} key={policy.policyId}>
            <PolicyProvider policy={policy}>
              <PolicyCard customerAccount={customerAccount} mb={lgBumps} />
            </PolicyProvider>
          </ProductContext.Provider>
        </PaymentMethodsProvider>
      )
    })

  return <Box {...props}>{sections}</Box>
}
